// src/components/admin/DeletionReasonCatalogPage.js
import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Input, Button, Table, Select, 
  message, Popconfirm, Space, Typography, Tag, Tooltip, Modal 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { catalogService } from '../../services/api'; // Importar el servicio
import { useAuth } from '../../context/AuthContext'; // Importar el hook para permisos

const { Title } = Typography;
const { Option } = Select;

const DeletionReasonCatalogPage = () => {
  const { user } = useAuth(); // Obtener el usuario para verificar permisos
  const [form] = Form.useForm();
  const [reasons, setReasons] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReason, setEditingReason] = useState(null); 

  // Definir constantes para los permisos (usando los nombres que planeamos)
  const permissions = user?.role?.profile?.permissions;
  const canViewPage = permissions?.pages?.viewAdminDeletionReasons;
  const canCreate = permissions?.actions?.deletionReasonCatalog?.canCreateReason;
  const canEdit = permissions?.actions?.deletionReasonCatalog?.canEditReason;
  const canDelete = permissions?.actions?.deletionReasonCatalog?.canDeleteReason;

  useEffect(() => {
    // Solo se cargan los datos si tiene permiso para ver la página
    if (canViewPage) {
      fetchReasons();
    }
  }, [canViewPage]);

  const fetchReasons = async () => {
    setLoading(true);
    try {
      // Llamar a la nueva función del servicio
      const response = await catalogService.getAllDeletionReasonsForAdmin();
      if (response.data.success) {
        setReasons(response.data.data);
      } else {
        message.error(response.data.message || 'Error al cargar el catálogo de motivos');
        setReasons([]);
      }
    } catch (error) {
      console.error('Error al cargar el catálogo de motivos:', error);
      setReasons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddReason = () => {
    setEditingReason(null); 
    form.resetFields(); 
    form.setFieldsValue({ status: 'activo' });
    setIsModalOpen(true); 
  };

  const handleEditReason = (record) => {
    setEditingReason(record);
    form.setFieldsValue({
      reason: record.reason,
      description: record.description,
      status: record.status,
    });
    setIsModalOpen(true);
  };

  const handleDeleteReason = async (reasonId) => {
    setLoading(true);
    try {
      const response = await catalogService.deleteDeletionReason(reasonId);
      if (response.data.success) {
        message.success('Motivo eliminado correctamente');
        fetchReasons(); 
      } else {
        message.error(response.data.message || 'Error al eliminar el motivo');
      }
    } catch (error) {
      console.error('Error al eliminar el motivo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReason = async (values) => {
    setLoading(true);
    const payload = { ...values };
    
    try {
      let response;
      if (editingReason) { 
        response = await catalogService.updateDeletionReason(editingReason._id, payload);
      } else { 
        response = await catalogService.createDeletionReason(payload);
      }

      if (response.data.success) {
        message.success(`Motivo ${editingReason ? 'actualizado' : 'guardado'} correctamente`);
        setIsModalOpen(false);
        setEditingReason(null);
        form.resetFields();
        fetchReasons(); 
      } else {
        message.error(response.data.message || `Error al ${editingReason ? 'actualizar' : 'guardar'} el motivo`);
      }
    } catch (error) {
      console.error(`Error al ${editingReason ? 'actualizar' : 'guardar'} el motivo:`, error);
    } finally {
      setLoading(false);
    }
  };


  const columns = [
    { 
      title: 'Motivo', 
      dataIndex: 'reason', 
      key: 'reason', 
      sorter: (a,b) => a.reason.localeCompare(b.reason) 
    },
    { 
      title: 'Descripción', 
      dataIndex: 'description', 
      key: 'description',
      sorter: (a,b) => (a.description || '').localeCompare(b.description || '') 
    },
    { 
      title: 'Estatus', 
      dataIndex: 'status', 
      key: 'status',
      width: 120,
      render: (status) => <Tag color={status === 'activo' ? 'green' : 'red'}>{status ? status.toUpperCase() : 'N/A'}</Tag>,
      filters: [
        { text: 'Activo', value: 'activo' },
        { text: 'Inactivo', value: 'inactivo' },
      ],
      onFilter: (value, record) => record.status.indexOf(value) === 0,
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          {canEdit && (
            <Tooltip title="Editar">
              <Button type="primary" shape="circle" icon={<EditOutlined />} onClick={() => handleEditReason(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title="¿Está seguro de eliminar este motivo?"
              onConfirm={() => handleDeleteReason(record._id)}
              okText="Sí"
              cancelText="No"
              placement="topRight"
            >
              <Tooltip title="Eliminar">
                <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!canViewPage) {
    return (
      <Card title={<Title level={4}>Catálogo de Motivos de Eliminación</Title>}>
        <p>No tiene permiso para acceder a este catálogo.</p>
      </Card>
    );
  }

  return (
    <Card title={<Title level={4}>Catálogo de Motivos de Eliminación</Title>}>
      {canCreate && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddReason}
          style={{ marginBottom: 16 }}
        >
          Agregar Nuevo Motivo
        </Button>
      )}

      <Modal
        title={editingReason ? "Editar Motivo" : "Agregar Nuevo Motivo"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingReason(null);
          form.resetFields();
        }}
        footer={null} 
        destroyOnClose 
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveReason}
          initialValues={{ status: 'activo' }} 
        >
          <Form.Item
            name="reason"
            label="Motivo"
            rules={[{ required: true, message: 'Por favor ingrese el motivo' }]}
          >
            <Input placeholder="Ej: Error de captura" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Descripción (Opcional)"
          >
            <Input.TextArea rows={3} placeholder="Descripción detallada del motivo" />
          </Form.Item>
          <Form.Item
            name="status"
            label="Estatus"
            rules={[{ required: true, message: 'Por favor seleccione un estatus' }]}
          >
            <Select placeholder="Seleccione un estatus">
              <Option value="activo">Activo</Option>
              <Option value="inactivo">Inactivo</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {editingReason ? "Actualizar Motivo" : "Guardar Motivo"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Table
        columns={columns}
        dataSource={reasons}
        rowKey="_id" 
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }} 
      />
    </Card>
  );
};

export default DeletionReasonCatalogPage;
